// lib/analytics/survey-surface.ts — which URL prefixes load PostHog's
// surveys.js client extension.
//
// The surveys extension (~32 KB fetched via /ingest at posthog-js init) only
// powers NATIVE popover surveys — today that's the Sean Ellis PMF survey
// (lib/posthog-surveys.ts), which targets signed-in product users. The NPS
// widget and the cancellation-reason survey are bespoke UI mirrored into
// PostHog SERVER-side (`survey sent` capture from /api/nps/submit and
// /api/billing/cancellation-intent) and never involve the client extension.
//
// So surveys.js is pure dead weight on the anonymous surfaces — marketing,
// ads, funnel, auth — which are exactly the pages PageSpeed measures: it was
// 81% unused JS loading inside the mobile LCP window on the homepage
// (PageSpeed mobile investigation, 2026-07). posthog-provider.tsx passes
// `disable_surveys: !isSurveySurface(pathname)` at init.
//
// The prefix list mirrors the route segments of app/(app) and
// app/(onboarding). __tests__/analytics/survey-surface.test.ts pins the
// lockstep: adding an authed route segment fails the test until it's added
// here (or deliberately excluded there).
//
// Init runs once per full page load, but some entries into the authed app
// are client-side navigations (the project-first funnel's /claim-callback
// soft-replaces to /projects/<id>). PostHogProvider therefore watches the
// pathname and re-enables + lazy-loads the surveys extension on the first
// navigation onto a survey surface (set_config + surveys.loadIfEnabled).

export const SURVEY_SURFACE_PREFIXES = [
  // app/(app)
  "/admin",
  "/approvals",
  "/billing",
  "/company-documents",
  "/compliance",
  "/compliance-primer",
  "/coshh",
  "/dashboard",
  "/documents",
  "/plant",
  "/portal",
  "/projects",
  "/referrals",
  "/saved-data",
  "/settings",
  "/subcontractors",
  "/team",
  "/workers",
  // app/(onboarding)
  "/onboarding",
  "/welcome",
  // app/(funnel) — /claim-callback is the signed-in post-signup hop that
  // router.replace()s straight into /projects/<id> WITHOUT a hard load. If
  // surveys were disabled when PostHog initialized there, posthog-js's
  // surveys manager never seeds its enabled-state from the remote config and
  // a later loadIfEnabled() no-ops (posthog-js 1.390.2, Codex review on
  // PR #1276) — so enable surveys AT INIT on this transient page. It's not a
  // PageSpeed-measured surface; the extra 32 KB there is fine.
  "/claim-callback",
] as const;

export function isSurveySurface(pathname: string): boolean {
  return SURVEY_SURFACE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}
